import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, RotateCcw, Square } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { RunStatusBadge } from '@/components/model-lab/badges'
import { CaseDrawer } from '@/components/model-lab/CaseDrawer'
import { CasesTable } from '@/components/model-lab/CasesTable'
import { NewRunDialog } from '@/components/model-lab/NewRunDialog'
import { Scoreboard } from '@/components/model-lab/Scoreboard'
import { useToast } from '@/hooks/use-toast'
import { useGHLLocationNames } from '@/hooks/useGHLDashboard'
import {
  useCancelModelLabRun,
  useModelLabCases,
  useModelLabLabels,
  useModelLabResults,
  useModelLabRun,
} from '@/hooks/useModelLab'
import { contestantKey, TASK_META, type LabelValue } from '@/lib/model-lab'

export default function ModelLabRunPage() {
  const { runId = '' } = useParams()
  const { toast } = useToast()
  const run = useModelLabRun(runId)
  const active = run.data?.status === 'queued' || run.data?.status === 'running'
  const cases = useModelLabCases(runId, active)
  const results = useModelLabResults(runId, active)
  const labels = useModelLabLabels(run.data?.location_id)
  const locationNames = useGHLLocationNames()
  const cancel = useCancelModelLabRun()
  const queryClient = useQueryClient()

  // Polling stops the moment the run reads finished; fetch once more so the
  // last few seconds of results aren't missing from the scoreboard.
  const wasActive = useRef(active)
  useEffect(() => {
    if (wasActive.current && !active) {
      queryClient.invalidateQueries({ queryKey: ['admin', 'model-lab', 'cases', runId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'model-lab', 'results', runId] })
    }
    wasActive.current = active
  }, [active, runId, queryClient])

  const [openCaseId, setOpenCaseId] = useState<string | null>(null)
  const [rerunOpen, setRerunOpen] = useState(false)

  const contestantKeys = useMemo(
    () => [...new Set((run.data?.contestants ?? []).map(contestantKey))],
    [run.data?.contestants]
  )
  const labelValues = useMemo(
    () => new Map<string, LabelValue>([...(labels.data ?? new Map()).entries()].map(([k, v]) => [k, v.label])),
    [labels.data]
  )
  const tasksWithCases = (run.data?.tasks ?? []).filter((t) => (cases.data ?? []).some((c) => c.task === t))

  if (run.isLoading) return <Skeleton className="h-64 w-full" />
  if (!run.data) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Run not found.</AlertDescription>
      </Alert>
    )
  }

  const done = results.data?.length ?? 0
  const total = run.data.total_calls
  const labelledCount = (cases.data ?? []).filter((c) => labels.data?.has(c.case_key)).length

  const onCancel = () =>
    cancel.mutate(runId, {
      onError: (error) => toast({ title: 'Could not cancel', description: (error as Error).message, variant: 'destructive' }),
    })

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <Link to="/admin/model-lab" className="flex items-center gap-1 text-sm text-muted-foreground hover:underline">
            <ArrowLeft className="h-3.5 w-3.5" /> Model Lab
          </Link>
          <h1 className="flex items-center gap-3 text-2xl font-bold">
            {locationNames.data?.get(run.data.location_id) ?? run.data.location_id}
            <RunStatusBadge status={run.data.status} />
          </h1>
          <p className="text-sm text-muted-foreground">
            Started {new Date(run.data.created_at).toLocaleString()} · {(cases.data ?? []).length} conversations ·{' '}
            {labelledCount} labelled
          </p>
        </div>
        <div className="flex gap-2">
          {active && (
            <Button variant="outline" onClick={onCancel} disabled={cancel.isPending}>
              <Square className="mr-1 h-4 w-4" /> Stop
            </Button>
          )}
          <Button variant="outline" onClick={() => setRerunOpen(true)}>
            <RotateCcw className="mr-1 h-4 w-4" /> Run again
          </Button>
        </div>
      </div>

      {run.data.error && (
        <Alert variant="destructive">
          <AlertDescription>{run.data.error}</AlertDescription>
        </Alert>
      )}

      {(active || (total > 0 && done < total)) && (
        <Card>
          <CardContent className="space-y-2 pt-6">
            <div className="flex justify-between text-sm">
              <span>{run.data.status === 'queued' || total === 0 ? 'Picking conversations…' : 'Running models…'}</span>
              <span className="text-muted-foreground">
                {done} / {total || '?'} calls
              </span>
            </div>
            <Progress value={total ? (done / total) * 100 : 0} />
          </CardContent>
        </Card>
      )}

      {tasksWithCases.length > 0 && (
        <Tabs defaultValue={tasksWithCases[0]}>
          <TabsList>
            {tasksWithCases.map((task) => (
              <TabsTrigger key={task} value={task}>
                {TASK_META[task].label}
              </TabsTrigger>
            ))}
          </TabsList>
          {tasksWithCases.map((task) => (
            <TabsContent key={task} value={task} className="space-y-4">
              <Scoreboard
                task={task}
                contestantKeys={contestantKeys}
                cases={cases.data ?? []}
                results={results.data ?? []}
                labels={labelValues}
              />
              <CasesTable
                task={task}
                contestantKeys={contestantKeys}
                cases={cases.data ?? []}
                results={results.data ?? []}
                labels={labelValues}
                onOpenCase={setOpenCaseId}
              />
            </TabsContent>
          ))}
        </Tabs>
      )}

      <CaseDrawer caseId={openCaseId} labels={labels.data ?? new Map()} onClose={() => setOpenCaseId(null)} />
      <NewRunDialog open={rerunOpen} onOpenChange={setRerunOpen} template={run.data} />
    </div>
  )
}
